import { Component, type ReactNode, type ErrorInfo } from "react";
import { ClientLogger } from "@/lib/clientLogger";
import { useTranslation } from "react-i18next";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Root-level error boundary. Catches crashes in routing, context providers,
 * and components above ChatErrorFallback. Shows error details + reload button
 * and fires telemetry so we know about it.
 */
class GlobalErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    ClientLogger.error("Global error boundary hit", {
      error,
      component: "GlobalErrorBoundary",
      action: "global-render",
      metadata: {
        componentStack: info.componentStack?.slice(0, 1000),
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return <GlobalErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

function GlobalErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-screen items-center justify-center bg-background p-4">
      <div className="space-y-4 text-center max-w-lg">
        <h1 className="font-semibold text-xl text-foreground">
          {t("error.boundaryFallback")}
        </h1>
        <details className="text-left text-xs text-muted-foreground">
          <summary className="cursor-pointer">{t("error.detailsToggle")}</summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border p-2">
            {(error?.message ?? "Unknown error").slice(0, 500)}
          </pre>
        </details>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          onClick={() => window.location.reload()}
        >
          {t("error.reloadPage")}
        </button>
      </div>
    </div>
  );
}

export { GlobalErrorBoundaryInner as GlobalErrorBoundary };
